import React, { useState, useEffect, useRef, useCallback } from 'react';
import theme from '../styles/theme';
import axialMriCache from '../services/axialMriCache';

const DEBUG = false;

// Component-scoped styles for the viewer chrome (slider thumb/track, HUD
// positioning, control bar). Kept identical in SagittalMRIViewer.jsx so the
// two panes look and feel consistent. The 16px thumb (radius 8) matches the
// thumbRadius=8 marker-compensation math below — do not change one without
// the other.
const viewerCss = `
.mri-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.mri-viewer--center {
  align-items: center;
  justify-content: center;
}
.mri-viewer__canvas {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mri-image-wrap {
  position: relative;
  max-width: 100%;
  max-height: 100%;
}
.mri-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.mri-hud {
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  white-space: nowrap;
}
.mri-hud--tl { top: 10px; left: 10px; }
.mri-hud--tr { top: 10px; right: 10px; }
.mri-hud__value {
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-primary);
}
.mri-viewer__controls {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  padding: 10px 14px;
  background: rgba(10, 12, 15, 0.72);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid var(--line-subtle);
  z-index: 10;
}
.mri-nav-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: var(--radius-full);
  font-size: 0.95rem;
  flex: none;
}
.mri-slider-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 520px;
  display: flex;
  align-items: center;
}
.mri-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 16px;
  margin: 0;
  background: transparent;
  cursor: pointer;
}
.mri-slider:disabled {
  cursor: default;
  opacity: 0.45;
}
.mri-slider:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: var(--radius-full);
}
.mri-slider::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.14);
}
.mri-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  margin-top: -6px;
  border-radius: var(--radius-full);
  background: var(--accent);
  border: 2px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  transition: transform var(--dur-fast) var(--ease-spring);
}
.mri-slider:hover:not(:disabled)::-webkit-slider-thumb { transform: scale(1.12); }
.mri-slider:active:not(:disabled)::-webkit-slider-thumb { transform: scale(0.96); }
.mri-slider::-moz-range-track {
  height: 4px;
  border-radius: var(--radius-full);
  background: rgba(255, 255, 255, 0.14);
}
.mri-slider::-moz-range-progress {
  height: 4px;
  border-radius: var(--radius-full);
  background: var(--accent);
}
.mri-slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: var(--radius-full);
  background: var(--accent);
  border: 2px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
}
.mri-slider-markers {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.mri-marker {
  position: absolute;
  top: 50%;
  margin-top: -4px;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full);
  border: 1px solid rgba(0, 0, 0, 0.55);
  transition: transform var(--dur-fast) var(--ease-spring),
    background-color var(--dur-fast) ease,
    box-shadow var(--dur-fast) ease;
}
.mri-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(5, 6, 8, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  color: var(--text-secondary);
  font-size: 0.86rem;
  text-align: center;
}
.mri-overlay p { margin: 0; }
.mri-overlay__progress { width: min(220px, 70%); }
.mri-overlay__pct {
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-tertiary);
}
.mri-error { max-width: min(440px, 86%); }
.mri-error__title {
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.mri-error p {
  margin: 2px 0;
  color: var(--text-secondary);
}
`;

/**
 * AxialMRIViewer component
 * Displays axial MRI slices with segmentation overlay for IVD-mapped slices
 * Uses axialImageCache for smooth scrolling with preloaded images
 */
function AxialMRIViewer({
  selectedLevel,
  ivdMapping,
  onSliceChange,
  externalSliceIndex
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentViewerIndex, setCurrentViewerIndex] = useState(0); // Index into availableSlices array
  const [availableSlices, setAvailableSlices] = useState([]); // Actual slice indices that exist as PNGs
  const [imageData, setImageData] = useState(null);
  const [segmentedSlices, setSegmentedSlices] = useState([]);
  const [ivdForCurrentSlice, setIvdForCurrentSlice] = useState([]);
  const [isPreloading, setIsPreloading] = useState(true);
  const [preloadProgress, setPreloadProgress] = useState(0);

  const containerRef = useRef(null);
  const lastScrollTime = useRef(0);
  // Track if current slice change was triggered by selectedLevel change (to prevent feedback loop)
  const isLevelTriggeredChange = useRef(false);
  // Track if we've handled the initial selection jump (to avoid overriding middle-start)
  const hasInitializedSelection = useRef(false);

  // Get actual slice index from viewer index
  const totalSlices = availableSlices.length;

  // Initialize axial viewer and load info using cache service
  useEffect(() => {
    const initializeAxial = async () => {
      try {
        setLoading(true);
        console.log('Initializing Axial MRI viewer with cache...');

        // Use cache service to load info
        const { axialInfo: info } = await axialMriCache.loadAxialInfo();

        // Use the available_slices array which contains actual slice indices
        const slices = info.available_slices || [];
        setAvailableSlices(slices);
        setSegmentedSlices(info.segmented_slices || []);

        console.log(`Available axial slices: ${slices.length}`, slices);
        console.log(`Segmented slices:`, info.segmented_slices);

        if (slices.length > 0) {
          // Start at closest segmented slice to middle instead of just geometric middle
          const middleIndex = Math.floor(slices.length / 2);
          let initialIndex = middleIndex;

          // Smart initialization: Find closest segmented slice
          if (info.segmented_slices && info.segmented_slices.length > 0) {
            const segmented = info.segmented_slices;
            // Find slice in segmented list closest to middle
            // Note: segmented contains actual slice indices
            let closestDist = Infinity;
            let closestSlice = -1;

            segmented.forEach(sliceIdx => {
              // Convert actual slice index to viewer index
              const vIdx = slices.indexOf(sliceIdx);
              if (vIdx !== -1) {
                const dist = Math.abs(vIdx - middleIndex);
                if (dist < closestDist) {
                  closestDist = dist;
                  closestSlice = sliceIdx;
                  initialIndex = vIdx;
                }
              }
            });
            console.log(`Smart Initialization: Selected slice ${closestSlice} (index ${initialIndex})`);
          }

          setCurrentViewerIndex(initialIndex);

          // Preload all available slices with progress tracking
          setIsPreloading(true);
          setPreloadProgress(0);
          console.log(`Starting to preload ${slices.length} axial MRI slices...`);

          try {
            await axialMriCache.preloadAllSlices((progress) => {
              setPreloadProgress(progress);
            });

            console.log('Axial MRI preloading complete');
            setIsPreloading(false);
            setPreloadProgress(100);
          } catch (err) {
            console.warn('Preload warning:', err);
            setIsPreloading(false);
          }
        } else {
          setIsPreloading(false);
        }

        console.log('Axial MRI info loaded:', info);
        setLoading(false);
      } catch (error) {
        console.error('Error initializing Axial MRI:', error);
        setError(`Error initializing Axial MRI: ${error.message}`);
        setLoading(false);
      }
    };

    initializeAxial();
  }, []);

  // Handle external slice index changes (from sagittal view clicks or dropdown)
  useEffect(() => {
    if (externalSliceIndex !== undefined) {
      // Find the viewer index for this actual slice index
      const viewerIdx = availableSlices.indexOf(externalSliceIndex);
      if (viewerIdx !== -1 && viewerIdx !== currentViewerIndex) {
        setCurrentViewerIndex(viewerIdx);
      }
    }
  }, [externalSliceIndex, availableSlices, currentViewerIndex]);

  // Jump to slice when selected level changes
  useEffect(() => {
    if (selectedLevel && ivdMapping && availableSlices.length > 0) {
      // Skip the initial auto-jump to respect the middle-slice initialization
      if (!hasInitializedSelection.current) {
        hasInitializedSelection.current = true;
        return;
      }

      // Convert level format: "L4-L5" -> "IVD_L4/L5"
      const ivdKey = `IVD_${selectedLevel.replace('-', '/')}`;
      const mappingInfo = ivdMapping[ivdKey];

      if (mappingInfo && mappingInfo.axial_slice !== undefined) {
        // Find the viewer index for this actual slice index
        const viewerIdx = availableSlices.indexOf(mappingInfo.axial_slice);
        if (viewerIdx !== -1) {
          // Mark this as a level-triggered change to prevent feedback loop
          isLevelTriggeredChange.current = true;
          setCurrentViewerIndex(viewerIdx);
          if (DEBUG) console.log(`Jumping to viewer index ${viewerIdx} (slice ${mappingInfo.axial_slice}) for ${selectedLevel}`);
        }
      }
    }
  }, [selectedLevel, ivdMapping, availableSlices]);

  // Load slice data when current viewer index changes - use cache for smooth scrolling
  useEffect(() => {
    const loadSlice = async () => {
      if (availableSlices.length === 0) return;

      const actualSliceIndex = availableSlices[currentViewerIndex];
      if (actualSliceIndex === undefined) return;

      // Check if this change was triggered by level selection (dropdown/centroid click)
      const wasLevelTriggered = isLevelTriggeredChange.current;
      // Reset the flag for next change
      isLevelTriggeredChange.current = false;

      // Check cache first for instant display
      const cachedData = axialMriCache.getSlice(actualSliceIndex, true);

      if (cachedData) {
        // Use cached data immediately - no loading state needed
        setImageData({
          url: cachedData.imageUrl,
          dimensions: cachedData.dimensions
        });
        setIvdForCurrentSlice(cachedData.ivdLevels || []);

        // Notify parent of slice change
        // Only pass ivdLevels if this was a user-initiated scroll (not level-triggered)
        // to prevent feedback loop: level change -> slice change -> level change -> ...
        if (onSliceChange) {
          onSliceChange(actualSliceIndex, wasLevelTriggered ? null : cachedData.ivdLevels);
        }

        if (DEBUG) console.log(`Used cached axial slice ${actualSliceIndex}, levelTriggered: ${wasLevelTriggered}`);
        return;
      }

      // Not in cache - need to fetch
      setLoading(true);
      setError(null);

      try {
        const data = await axialMriCache.fetchSlice(actualSliceIndex, true);

        setImageData({
          url: data.imageUrl,
          dimensions: data.dimensions
        });
        setIvdForCurrentSlice(data.ivdLevels || []);

        // Notify parent of slice change
        // Only pass ivdLevels if this was a user-initiated scroll (not level-triggered)
        if (onSliceChange) {
          onSliceChange(actualSliceIndex, wasLevelTriggered ? null : data.ivdLevels);
        }

        if (DEBUG) console.log(`Fetched axial slice ${actualSliceIndex}, levelTriggered: ${wasLevelTriggered}`);
      } catch (error) {
        console.error(`Error loading axial slice ${actualSliceIndex}:`, error);
        setError(`Error loading slice: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    loadSlice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewerIndex, availableSlices]); // Note: onSliceChange intentionally omitted to prevent loops

  // Handle slice slider change
  const handleSliderChange = (e) => {
    if (!isPreloading) {
      setCurrentViewerIndex(parseInt(e.target.value));
    }
  };

  // Handle mouse wheel for scrolling - optimized for smooth scrolling
  const handleWheel = useCallback((e) => {
    e.preventDefault();

    // Disable scrolling while preloading
    if (isPreloading) return;

    // Throttle scroll events for smoother performance
    const now = Date.now();
    if (now - lastScrollTime.current < 30) {
      return; // Skip if less than 30ms since last scroll
    }
    lastScrollTime.current = now;

    const step = e.shiftKey ? 5 : 1;
    const delta = e.deltaY > 0 ? -step : step; // Reversed direction: Scroll DOWN (positive) -> Decrease Index

    setCurrentViewerIndex(prev => {
      const newIndex = prev + delta;
      return Math.max(0, Math.min(totalSlices - 1, newIndex));
    });
  }, [totalSlices, isPreloading]);

  // Add wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [handleWheel]);

  // Navigate slices
  const navigateSlice = (delta) => {
    const newIndex = Math.max(0, Math.min(totalSlices - 1, currentViewerIndex + delta));
    setCurrentViewerIndex(newIndex);
  };

  // Get the actual slice index for display and checks
  const currentSliceIndex = availableSlices[currentViewerIndex] ?? 0;

  // Check if current slice has segmentation
  const hasSegmentation = segmentedSlices.includes(currentSliceIndex);

  // Check if current slice is an IVD slice
  const isIvdSlice = ivdForCurrentSlice.length > 0;

  if (error && totalSlices === 0) {
    return (
      <div className="viewer mri-viewer mri-viewer--center">
        <style>{viewerCss}</style>
        <div className="alert alert--danger mri-error">
          <div>
            <h3 className="mri-error__title">Error Loading Axial MRI</h3>
            <p>{error}</p>
            <p>Make sure axial data has been preprocessed.</p>
          </div>
        </div>
      </div>
    );
  }

  if (totalSlices === 0 && !loading) {
    return (
      <div className="viewer mri-viewer mri-viewer--center">
        <style>{viewerCss}</style>
        <div className="empty">
          <p className="empty__title">No axial MRI data available</p>
          <p className="empty__sub">
            Upload and preprocess axial DICOM files to view
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer mri-viewer" ref={containerRef}>
      <style>{viewerCss}</style>
      {isPreloading && (
        <div className="mri-overlay">
          <div className="spinner"></div>
          <p>Loading MRI data...</p>
          <div className="progress mri-overlay__progress">
            <div
              className="progress__fill"
              style={{ width: `${preloadProgress}%` }}
            />
          </div>
          <p className="mri-overlay__pct">{Math.round(preloadProgress)}%</p>
        </div>
      )}

      {loading && !imageData && !isPreloading && (
        <div className="mri-overlay">
          <div className="spinner"></div>
          <p>Loading slice {currentViewerIndex + 1}/{totalSlices}...</p>
        </div>
      )}

      <div className="mri-viewer__canvas">
        {/* Text Overlay */}
        <div className="viewer__hud mri-hud mri-hud--tl">
          <span>Axial MRI — Slice</span>
          <span className="mri-hud__value">{currentViewerIndex + 1}/{totalSlices}</span>
          {isIvdSlice && (
            <span className="badge badge--warning">
              {ivdForCurrentSlice.join(', ')}
            </span>
          )}
        </div>

        {/* Segmentation indicator */}
        {hasSegmentation && (
          <div className="viewer__hud mri-hud mri-hud--tr">
            <span className="status-dot" style={{ background: 'var(--success)' }} />
            Segmented
          </div>
        )}

        <div className="mri-image-wrap">
          {imageData && !isPreloading && (
            <img
              src={imageData.url}
              alt="Axial MRI"
              className="mri-image"
              onError={() => {
                setError('Failed to load axial MRI image');
              }}
            />
          )}
        </div>
      </div>

      {/* Slice Navigation Controls */}
      <div className="mri-viewer__controls">
        <button
          type="button"
          className="btn btn--secondary mri-nav-btn"
          disabled={currentViewerIndex <= 0 || isPreloading}
          onClick={() => navigateSlice(-1)}
        >
          &lt;
        </button>

        <div className="mri-slider-wrap">
          <input
            type="range"
            min="0"
            max={totalSlices - 1}
            value={currentViewerIndex}
            onChange={handleSliderChange}
            className="mri-slider"
            disabled={isPreloading}
          />
          {/* IVD markers on slider - show at each position since all slices are IVD slices */}
          <div className="mri-slider-markers">
            {/* IVD markers on slider - only show for mapped IVD slices */}
            {ivdMapping && Object.entries(ivdMapping).map(([name, info]) => {
              const sliceIdx = info.axial_slice;
              if (sliceIdx === undefined) return null;

              // Find the viewer index (position in availableSlices) for this slice
              const viewerIdx = availableSlices.indexOf(sliceIdx);
              if (viewerIdx === -1) return null;

              const thumbRadius = 8;
              const percentage = (viewerIdx / Math.max(totalSlices - 1, 1)) * 100;
              const isCurrentSlice = viewerIdx === currentViewerIndex;

              return (
                <div
                  key={name}
                  className="mri-marker"
                  style={{
                    left: `calc(${percentage}% + ${thumbRadius - (percentage / 100) * (thumbRadius * 2)}px)`,
                    backgroundColor: isCurrentSlice ? theme.colors.secondary.main : theme.colors.primary.light,
                    transform: `translateX(-50%) scale(${isCurrentSlice ? 1.3 : 1})`,
                    boxShadow: isCurrentSlice
                      ? `0 0 6px ${theme.colors.secondary.main}`
                      : '0 1px 3px rgba(0, 0, 0, 0.4)'
                  }}
                  title={name}
                />
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="btn btn--secondary mri-nav-btn"
          disabled={currentViewerIndex >= totalSlices - 1 || isPreloading}
          onClick={() => navigateSlice(1)}
        >
          &gt;
        </button>
      </div>
    </div>
  );
}

export default AxialMRIViewer;
